import exceptions from '../exceptions';
import { IS_PRODUCTION } from '../constants/crux';
import { X_CL_APP_ACCT_USER_GUID } from '../constants/httpHeaders';
import Store from '../store';
import { joiningUserInstance } from '../helpers/JoiningUser';
import CruxProxy from './cruxProxy';

const LINKED_ACCOUNTS_PATH = '/api/clapi/accounts';

// Standard Fetch API credentials policy — sends cookies with cross-origin requests
const FETCH_CREDENTIALS_MODE = 'include';

const sanitizeForLog = (value) => {
    let str;
    try {
        str = typeof value === 'string' ? value : JSON.stringify(value);
    } catch {
        str = String(value);
    }
    return str?.replace(/[\r\n\t]/g, ' ')?.substring(0, 500) ?? '';
};

const timeoutHandler = (timeout) => {
    // setup timeout handler
    const controller = new AbortController();
    const fetchTimeout = setTimeout(() => controller.abort(), timeout);
    return {
        signal: controller.signal,
        fetchTimeout,
    };
};

const getTimeouts = () => {
    try {
        const config = Store?.store?.getState()?.config;
        const timeouts = config?.get?.('timeouts');
        if (timeouts && typeof timeouts === 'object' && timeouts.small && timeouts.large) {
            return timeouts;
        }
    } catch (error) {
        console.warn(sanitizeForLog(error?.message || error));
    }
    // Fallback defaults
    return {
        small: 10000, // matches what's in the API
        large: 30000,
    };
};
function ApiError(message, data, status) {
    let response = null;
    let isObject = false;

    // We are trying to parse response
    try {
        response = JSON.parse(data);
        isObject = true;
    } catch (e) {
        response = data;
    }

    return {
        response,
        message,
        status,
        toString: () => `${message}\nResponse:\n${isObject ? JSON.stringify(response, null, 2) : response}`,
    };
}

const urlInit = (pathParam, isProxy) => {
    if (!IS_PRODUCTION && isProxy) {
        return `http://${window.location.hostname}:8080${pathParam}`;
    }
    return pathParam;
};

// Execute API call
// Signal {AbortSignal} - dependency on handling timeouts
// Pair this request with timeoutHandler when handling timeouts
const executeRequest = (url, options, signal) => fetch(url, { signal, ...options }).catch((error) => {
    const _error = error.name === 'AbortError' ? exceptions.REQUEST_TIMEOUT.message : error;
    throw new Error(_error);
});

const getUrlInit = (path) => {
    return urlInit(path, true);
}

const logoutInitiated = () => !!logoutInProgress[0];

// An array of size 1 which value is used as a flag to indicate that logout is in-progress.
// The default value of logoutInProgress[0] when the array is created is 0. We're setting the value to 1 with the help
// of the standard JavaScript Atomics functions which guarantee that the logout will be initiated only once.
const logoutInProgress = new Uint8Array(1);
console.log('Initializing api/index.js');

const isFile = body => typeof window !== 'undefined' &&
    (body instanceof File || body instanceof FormData);
const getIdentityHeaders = () => {
    try {
        // During the post-join flow, joiningUserInstance holds the in-progress account GUID.
        // The store's selectedClAppAccountUserGuid still references the old (deactivated) account,
        // so the joining instance must take precedence.
        const joiningGuid = joiningUserInstance?.clAppAcctUsrGuid;
        const storeGuid = Store?.store?.getState()?.linkedAccounts?.get?.('selectedClAppAccountUserGuid');
        const clAppAccountUserGuid = joiningGuid || storeGuid;

        const headers = {};
        if (clAppAccountUserGuid) {
            headers[X_CL_APP_ACCT_USER_GUID] = clAppAccountUserGuid;
        }
        return headers;
    } catch {
        return {};
    }
};

const getRequestOptions = (headerOptions) => {
    // Define default options
    const defaultOptions = { credentials: FETCH_CREDENTIALS_MODE };
    const options = {
        // Merge options
        ...defaultOptions,
        ...headerOptions,
        // Merge headers
        headers: {
            ...getIdentityHeaders(),
            ...headerOptions.headers,
        },
    };
    const _isFile = isFile(options.body);

    // Stringify JSON data
    // If body is not a file
    if (options.body && typeof options.body === 'object' && !_isFile) {
        options.body = JSON.stringify(options.body);
    } else if (_isFile) {
        // Deleting content-type header when file is being passed
        // Browser will assign it automatically.
        delete options.headers['Content-Type'];
    }

    return options;
}
const handleResponseDataType = (fetchTimeout, response) => {
    // Clears timeout once we get a successful response
    clearTimeout(fetchTimeout);

    // Check for error HTTP error codes
    if (response.status < 200 || response.status >= 300) {
        // Get response as text
        return response.text();
    }
    // Get response as json or blob
    const contentType = response.headers.get('content-type');
    if (contentType.startsWith('image/')) {
        return response.blob();
    }
    else if (contentType.startsWith('text/')) {
        return response.text();
    }
    else if (contentType.endsWith('/pdf')) {
        return response.arrayBuffer();
    }
    return response.json();
};
const handleSession = (requestOptions) => (response) => {
    if (((response.original.status === 401
                && requestOptions.path !== '/api/logout'
                && response.parsed.includes('unauthenticated_session_error'))
            || (response.original.status === 500
                && requestOptions.path !== '/api/logout'
                && response.parsed.includes('auth_session_error')))
        && Atomics.exchange(logoutInProgress, 0, 1) === 0) {

        CruxProxy.logout();
    }

    // Check for HTTP error codes
    if (response.original.status < 200 || response.original.status >= 300) {
        // Throw error
        throw response.parsed;
    }

    // Request succeeded
    return response.parsed;
};
const initiateSessionApiCall = (requestOptions = {}, headerOptions = {}, abortOptions = null) => {

    // Define default headers
    const options = getRequestOptions(headerOptions);
    // Build Url
    // for local development, let's skip the proxy pass
    const url = urlInit(requestOptions.path, requestOptions.isProxy || requestOptions.protected);

    // Detect if we are uploading a file.
    // Photo upload requires a formData to pass the file as a parameter
    // We need to be cautious whenever we use formData for our POST requests,
    // for now, it is only being used to send files to our APIs, using formData to pass json object
    // will be treated as file and will not work accordingly.
    // If in case in the future we need to use formData to send json object,
    // we need to find another way to detect file inside formData

    // Variable which will be used for storing response
    let response = null;

    // Set timeout
    const timeout = requestOptions.timeout ?? getTimeouts().small;

    // setup timeout handler
    const { signal, fetchTimeout } = abortOptions ?? timeoutHandler(timeout);
    return executeRequest(url, options, signal)
        .then((_response) => {
            // Saving response for later use in lower scopes
            response = _response;
            return handleResponseDataType(fetchTimeout, _response)
        })
        .then(parsed => ({
            original: response,
            parsed,
        }))
        .then(handleSession(requestOptions))
        .catch((error) => {
            // Throw custom API error
            // If response exists it means HTTP error occurred
            if (response) {
                if (response.status >= 200 && response.status < 300) {
                    return null;
                }
                throw ApiError(`Request failed with status ${response.status}.`, error, response.status);
            } else {
                throw ApiError(error.toString(), null, 'REQUEST_FAILED');
            }
        });
};

export default {
    getUrlInit,
    LINKED_ACCOUNTS_PATH,
    executeRequest,  // this is used only internally and from index.spec.js
    initiateSessionApiCall,
    getTimeouts,
    timeoutHandler, // this is used only internally and from index.spec.js
    logoutInitiated,
    sanitizeForLog,
};
